<template>
  <div class="cpm-dashboard my-10 px-2">
    <div class="d-flex justify-space-between" :style="{ position: 'relative' }">
      <!-- left -->

      <div class="d-flex">
        <!-- clear filters (admin) -->
        <div v-if="isAdmin || isManager" class="text-center">
          <span class="overline"> Clear </span>
          <div class="mb-4">
            <v-btn
              :min-width="laptop ? '40px' : '42px'"
              height="38px"
              class="elevation-2 px-0"
              color="secondary"
              @click="clearFilter(false)"
            >
              <v-icon>mdi-filter-remove</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- pay period filter -->
        <div :class="{ 'ml-lg-3 ml-md-2': isAdmin || isManager }">
          <span class="overline"> Pay Period </span>
          <div class="d-flex mb-4">
            <v-autocomplete
              class="cpm-dashboard__month-selector selector mr-2"
              :items="monthOptions"
              v-model="selectedMonth"
              solo
              dense
              hide-details
              @change="handleSelectedPeriod($event, 'ppm')"
            ></v-autocomplete>
            <v-autocomplete
              class="cpm-dashboard__year-selector selector"
              :items="yearOptions"
              v-model="selectedYear"
              solo
              dense
              hide-details
              @change="handleSelectedPeriod($event, 'ppy')"
            >
            </v-autocomplete>
          </div>
        </div>

        <!-- request date filter -->
        <div class="ml-lg-3 ml-md-2 text-center">
          <span class="overline"> Request Date </span>
          <div class="d-flex align-end mb-4">
            <v-menu
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              solo-inverted
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filterRequestDate"
                  :class="{ laptop }"
                  class="cpm-dashboard__date-selector selector"
                  solo
                  dense
                  readonly
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="clearFilter('RequestDate')"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filterRequestDate"
                @input="handleFilterQuery('RequestDate', $event)"
              ></v-date-picker>
            </v-menu>
          </div>
        </div>

        <!-- delivery date filter -->
        <div class="ml-lg-3 ml-md-2 text-center">
          <span class="overline">
            {{ isAdmin || isManager ? "Delivery Date" : "Due Date" }}
          </span>
          <div class="d-flex align-end mb-4">
            <v-menu
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              solo-inverted
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filterDeliveryDate"
                  :class="{ laptop }"
                  class="cpm-dashboard__date-selector selector"
                  solo
                  dense
                  readonly
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="clearFilter('DeliveryDate')"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filterDeliveryDate"
                @input="handleFilterQuery('DeliveryDate', $event)"
              ></v-date-picker>
            </v-menu>
          </div>
        </div>

        <!-- project filter -->
        <div v-if="isAdmin || isManager" class="ml-lg-3 ml-md-2 text-center">
          <span class="overline"> Project </span>
          <div>
            <v-autocomplete
              class="cpm-dashboard__project-selector selector"
              :class="{ laptop, laptopSmaller }"
              :items="projects"
              v-model="filterProject"
              solo
              dense
              hide-details
              :loading="dropdownsLoading"
              @input="handleFilterQuery('Project', $event)"
            ></v-autocomplete>
          </div>
        </div>

        <!-- GEO filter -->
        <transition>
          <div
            v-if="
              ((!selectedDashboardRows.length && laptopSmaller) ||
                !laptopSmaller) &&
              (isAdmin || isManager)
            "
            v-show="(!expandMenu && laptopSmaller) || !laptopSmaller"
            class="ml-lg-3 ml-md-2 text-center"
          >
            <span class="overline"> GEO </span>
            <div class="mb-4">
              <v-autocomplete
                class="cpm-dashboard__geo-selector selector"
                :items="geos"
                v-model="filterGeo"
                solo
                dense
                hide-details
                :loading="dropdownsLoading"
                @input="handleFilterQuery('Geo', $event)"
              ></v-autocomplete>
            </div>
          </div>
        </transition>

        <!-- person filter -->
        <transition>
          <div
            v-if="
              ((!selectedDashboardRows.length && laptop) || !laptop) &&
              (isAdmin || isManager ? earningsMode !== 0 : true)
            "
            v-show="isAdmin || isManager ? !expandMenu : true"
            class="ml-lg-3 ml-md-2 text-center"
          >
            <span class="overline">
              {{ "Role".padStart(isAdmin || isManager ? 13 : 4, "Person / ") }}
            </span>
            <div class="d-flex mb-4">
              <v-autocomplete
                v-if="isAdmin || isManager"
                :class="{ laptop, laptopSmaller }"
                class="cpm-dashboard__person-selector selector mr-2"
                :items="employees"
                v-model="filterPerson"
                solo
                dense
                hide-details
                :loading="dropdownsLoading"
                @input="handleFilterQuery('Person', $event)"
              ></v-autocomplete>
              <v-autocomplete
                class="cpm-dashboard__role-selector selector"
                :items="roles"
                v-model="filterRole"
                solo
                dense
                hide-details
                :disabled="isAdmin || isManager ? !filterPerson : false"
                @input="handleFilterQuery('Role', $event)"
              >
              </v-autocomplete>
            </div>
          </div>
        </transition>

        <!-- status filter -->
        <transition>
          <div
            v-if="
              !isAdmin &&
              !isManager &&
              ((!selectedDashboardRows.length && laptop) || !laptop)
            "
            class="ml-lg-3 ml-md-2 text-center"
          >
            <span class="overline"> Status </span>
            <div class="d-flex mb-4">
              <v-autocomplete
                class="cpm-dashboard__status-selector selector"
                :items="statusOptions"
                v-model="filterStatus"
                item-text="value"
                solo
                dense
                hide-details
                :loading="dropdownsLoading"
                @input="handleFilterQuery('Status', $event)"
              >
              </v-autocomplete>
            </div>
          </div>
        </transition>

        <!-- clear filters (freelancer) -->
        <transition>
          <div
            v-if="!isAdmin && !isManager"
            v-show="laptopSmaller ? !expandMenu : true"
            class="ml-lg-3 ml-md-2 text-center"
          >
            <span class="overline"> Clear </span>
            <div class="mb-4">
              <v-btn
                :min-width="laptop ? '40px' : '42px'"
                height="38px"
                class="elevation-2 px-0"
                color="secondary"
                @click="clearFilter(false)"
              >
                <v-icon>mdi-filter-remove</v-icon>
              </v-btn>
            </div>
          </div>
        </transition>

        <!-- user's earnings -->
        <transition>
          <div
            v-if="!selectedDashboardRows.length"
            v-show="
              isAdmin || isManager ? !expandMenu : laptop ? !expandMenu : true
            "
            class="ml-lg-3 ml-md-2 text-center"
            :class="{
              'flex-column justify-end':
                (isAdmin || isManager) && earningsMode !== 0,
            }"
            :style="{
              display:
                (isAdmin || isManager) && earningsMode !== 0 ? 'flex' : 'block',
            }"
          >
            <span
              v-if="isAdmin || isManager ? earningsMode === 0 : true"
              class="overline"
            >
              {{
                `${
                  isAdmin || isManager
                    ? "My Monthly Invoice"
                    : `${filtersBadge ? "Filtered" : "Monthly"} Invoice Amount`
                }`
              }}
            </span>
            <div class="d-flex mb-4">
              <v-badge
                bordered
                color="secondary"
                overlap
                :value="filtersBadge"
                :content="filtersBadge"
              >
                <v-btn-toggle
                  class="elevation-2"
                  color="accent"
                  dense
                  v-model="earningsMode"
                >
                  <v-btn
                    min-width="fit-content"
                    :width="
                      earningsMode === 0 || isAdmin || isManager
                        ? 'fit-content'
                        : '200px'
                    "
                    height="38px"
                    :loading="showTableLoader"
                    ><v-icon color="#000000de">mdi-currency-usd</v-icon
                    ><span>{{
                      earningsMode === 0
                        ? `Total: ${$refs.dashboardTable.totalEarnings}`
                        : isAdmin || isManager
                        ? ""
                        : "Invoice"
                    }}</span></v-btn
                  >
                </v-btn-toggle>
              </v-badge>
              <v-btn
                v-if="earningsMode === 0"
                height="38px"
                :min-width="laptop ? '40px' : '42px'"
                class="elevation-2 px-0 ml-2"
                color="secondary"
                :disabled="!filteredData().length"
                @click="downloadInvoice"
              >
                <v-icon>mdi-tray-arrow-down</v-icon>
              </v-btn>
            </div>
          </div>
        </transition>

        <!-- bulk status assign -->
        <transition>
          <div
            class="ml-lg-3 ml-md-2 text-center d-flex"
            v-if="selectedDashboardRows.length > 0 && !expandMenu"
          >
            <div>
              <span class="overline"> Bulk Status Assign </span>
              <div class="d-flex align-end mb-4">
                <v-autocomplete
                  v-model="bulkStatusValue"
                  class="cpm-dashboard__status-selector selector"
                  solo
                  dense
                  hide-details
                  :items="
                    isAdmin || isManager ? statusOptions : freelancerStatuses
                  "
                  item-text="value"
                  @input="handleBulkStatusSelect"
                ></v-autocomplete>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- right -->
      <div class="d-flex flex-column justify-start align-end my-0">
        <!-- search -->
        <span class="overline" :style="{ opacity: 0, userSelect: 'none' }"
          >Search</span
        >
        <div class="mb-4">
          <v-badge
            :content="laptopSmaller ? '...' : filterSearchBadge"
            :value="filterSearch && !expandMenu"
            color="secondary"
            overlap
          >
            <v-btn
              height="38px"
              min-width="48px"
              class="elevation-2 px-0"
              outlined
              color="accent"
              @click="expandMenu = !expandMenu"
            >
              <v-icon v-if="expandMenu">mdi-magnify-minus-outline</v-icon>
              <v-icon v-else>mdi-magnify-plus-outline</v-icon>
            </v-btn>
          </v-badge>
          <v-expand-x-transition>
            <v-text-field
              :style="{ position: 'absolute', bottom: '16px', right: '60px' }"
              v-show="expandMenu"
              v-model="filterSearch"
              class="cpm-dashboard__search-selector selector"
              solo
              dense
              hide-details
              clearable
            />
          </v-expand-x-transition>
        </div>
      </div>
    </div>
    <!-- table -->
    <v-card class="cpm-dashboard__table-card">
      <v-skeleton-loader
        v-if="showTableLoader"
        type="table"
        height="100vh"
      ></v-skeleton-loader>
      <CPMDashboardTable
        ref="dashboardTable"
        v-if="tableData && !showTableLoader"
        :tableData="tableData"
        :tableFields="tableFields"
        :filterDeliveryDate="filterDeliveryDate"
        :filterRequestDate="filterRequestDate"
        :filterSearch="filterSearch"
        :filterProject="filterProject ? filterProject : ''"
        :filterPerson="filterPerson ? filterPerson : ''"
        :filterRole="filterRole ? filterRole : ''"
        :filterGeo="filterGeo ? filterGeo : ''"
        :filterStatus="filterStatus ? filterStatus : ''"
        :isStatusUpdating="isStatusUpdating"
        :statuses="isAdmin || isManager ? statusOptions : freelancerStatuses"
        :statusesLoading="dropdownsLoading"
        :showEarnings="earningsMode === 0 ? 1 : 0"
      />
    </v-card>
  </div>
</template>

<script>
// vuex
import { mapGetters, mapActions } from "vuex";
// internal
import {
  getAssignments,
  updateMultipleDBRowStatus,
  appIDs,
  getEmployeesData,
  getFieldInfosFromDB,
  getReportData,
} from "@/utils/quickbaseUtils";
import { customBreakpoint, timeUnits } from "@/utils/mixins";
import { STATUS_COLORS, ROUTES } from "@/utils/constants";
import { generateInvoice } from "@/utils/dashboardsUtils";
// component
import CPMDashboardTable from "@/components/CPMDashboard/CPMDashboardTable";

export default {
  name: "CPMDashboard",
  components: {
    CPMDashboardTable,
  },
  data() {
    return {
      bulkStatusValue: "",
      showTableLoader: true,
      tableData: null,
      tableFields: null,
      selectedMonth: "",
      selectedYear: "",
      filterDeliveryDate: null,
      filterRequestDate: null,
      filterSearch: "",
      expandMenu: false,
      projects: [],
      filterProject: "",
      dropdownsLoading: true,
      employees: [],
      filterPerson: "",
      roles: ["Primary", "Reviewer"],
      filterRole: "",
      geos: [],
      filterGeo: "",
      filterStatus: "",
      isStatusUpdating: false,
      statusOptions: [],
      earningsMode: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["user", "isAdmin", "isManager"]),
    ...mapGetters("fcBot", ["selectedDashboardRows"]),
    filterSearchBadge() {
      return this.filterSearch?.length <= 6
        ? this.filterSearch
        : `${this.filterSearch?.slice(0, 6)}...`;
    },
    filtersBadge() {
      return Object.entries(this.$route.query).filter(
        (el) => el[0] !== "ppm" && el[0] !== "ppy" && el[1] !== null
      ).length;
    },
    freelancerStatuses() {
      return this.statusOptions.map((el) => ({
        ...el,
        disabled: el.value === "Delivered",
      }));
    },
  },
  mixins: [customBreakpoint, timeUnits],
  watch: {
    filterPerson(newValue) {
      if ((this.isAdmin || this.isManager) && newValue === null)
        this.filterRole = "";
    },
    filterSearch(newValue) {
      const currentQuery = this.$route.query;
      const newQuery =
        newValue !== ""
          ? {
              ...currentQuery,
              Search: newValue,
            }
          : _.omit(currentQuery, ["Search"]);

      this.$router
        .push({ path: `/${ROUTES.cpmDashboard}`, query: newQuery })
        .catch(() => {}); // Avoided redundant navigation error handler
    },
    earningsMode(newVal) {
      if (this.isAdmin || this.isManager)
        this.filterPerson = newVal === 0 ? this.user.Name : "";
    },
  },
  mounted() {
    this.handleQuery();
    this.handleDropdowns();
  },
  methods: {
    ...mapActions("fcBot", ["setSelectedDashboardRows"]),
    handleSelectedPeriod(value, key, queryUpdate) {
      const query = { ...this.$route.query };

      if (value) {
        query[key] = value;
      } else {
        delete query[key];
      }

      this.$router
        .push({ path: `/${ROUTES.cpmDashboard}`, query })
        .catch(() => {}); // Avoided redundant navigation error handler

      if (!queryUpdate) this.getTableData();
    },
    handleDueValue(item) {
      if (item["Person-Primary"] === this.user.Name)
        return new Date(
          `${item.PrimaryDueDate.replace(/-/g, "/")} ${
            item["PrimaryDue(TimeOfDay)"]
          }`
        );
      if (item["Person-Reviewer"] === this.user.Name)
        return new Date(
          `${item.ReviewerDueDate.replace(/-/g, "/")} ${
            item["ReviewDue(TimeOfDay)"]
          }`
        );
    },
    getTableData() {
      this.showTableLoader = true;
      getAssignments({
        personName: this.isAdmin || this.isManager ? false : this.user.Name,
        payPeriod: `${this.selectedMonth} ${this.selectedYear}`,
        cpmMode: true,
      })
        .then((resp) => {
          this.tableData = resp.data.map((el) => {
            return {
              ...el,
              DueTime: this.handleDueValue(el),
              checked: false,
            };
          });
          this.tableFields = resp.fields;
          this.tableFields.push(
            {
              text: "Due Time",
              value: "DueTime",
            },
            {
              text: "Total Internal",
              value: "TotalInternal",
            },
            {
              text: "Inquiry",
              value: "Inquiry",
            }
          );
          // get user's earnings data
          getAssignments({
            payPeriod: `${this.selectedMonth} ${this.selectedYear}`,
            personName: this.user.Name,
            earningsMode: true,
          })
            .then((earnings) => {
              earnings.data.forEach((earn) => {
                const earningID = earn.uniqueID.split(
                    appIDs.cueProjectManager.assignments
                  )[0],
                  earningRow = this.tableData.find(
                    (row) => row["RecordID#"] == earningID
                  ),
                  earningRowIndex = this.tableData.indexOf(earningRow);
                if (earningRowIndex >= 0)
                  Object.assign(this.tableData[earningRowIndex], {
                    TotalInternal: earn.TotalInternal,
                  });
              });
              this.showTableLoader = false;
            })
            .catch((err) => {
              console.error(err);
              this.showTableLoader = false;
            });
        })
        .catch((err) => {
          console.error(err);
          this.showTableLoader = false;
        });
    },
    handleFilterQuery(param, data) {
      const currentQuery = this.$route.query;

      const newQuery = {
        ...currentQuery,
        [param]: data,
      };
      this.$router
        .push({ path: `/${ROUTES.cpmDashboard}`, query: newQuery })
        .catch(() => {}); // Avoided redundant navigation error handler

      this[`filter${param}`] = data;
    },
    clearFilter(date) {
      let query = this.$route.query;

      if (date) {
        query = _.omit(query, [date]);
        this[`filter${date}`] = null;
      } else {
        query = _.pick(query, ["ppm", "ppy"]);
        this.filterProject =
          this.filterRole =
          this.filterStatus =
          this.filterGeo =
          this.filterSearch =
            "";
        this.filterPerson =
          (this.isAdmin || this.isManager) && this.earningsMode === 0
            ? this.filterPerson
            : "";
      }

      if (!date) this.filterRequestDate = this.filterDeliveryDate = null;

      this.$router
        .push({ path: `/${ROUTES.cpmDashboard}`, query })
        .catch(() => {}); // Avoided redundant navigation error handler
    },
    handleBulkStatusSelect(status) {
      this.isStatusUpdating = true;
      updateMultipleDBRowStatus({
        rows: this.selectedDashboardRows,
        status,
        cpmMode: true,
      })
        .then(() => {
          this.isStatusUpdating = false;
          this.bulkStatusValue = "";
          this.getTableData();
          this.setSelectedDashboardRows([]);
        })
        .catch((err) => {
          console.error(err);
          this.isStatusUpdating = false;
        });
    },
    getEmployees() {
      return new Promise((resolve, reject) => {
        getEmployeesData({ namesOnly: true })
          .then((names) => resolve((this.employees = names)))
          .catch((err) => reject(console.error(err)));
      });
    },
    getGeos() {
      return new Promise((resolve, reject) => {
        getFieldInfosFromDB({
          fieldID: 44,
          tableID: appIDs.cueProjectManager.assignments,
        })
          .then((geos) => resolve((this.geos = geos)))
          .catch((err) => reject(console.error(err)));
      });
    },
    getProjects() {
      return new Promise((resolve, reject) => {
        getReportData({
          reportID: 1,
          tableID: appIDs.cueProjectManager.projects,
        })
          .then((projects) =>
            resolve(
              (this.projects = projects.map((el) => {
                return el.ProjectName;
              }))
            )
          )
          .catch((err) => reject(console.error(err)));
      });
    },
    getStatuses() {
      return new Promise((resolve, reject) => {
        getFieldInfosFromDB({
          fieldID: 110,
          tableID: appIDs.cueProjectManager.assignments,
        })
          .then((data) =>
            resolve(
              (this.statusOptions = data.map((el) => {
                return {
                  value: el,
                  color: STATUS_COLORS.find((color) =>
                    color.statuses.includes(el)
                  )?.value,
                };
              }))
            )
          )
          .catch((err) => reject(console.error(err)));
      });
    },
    handleDropdowns() {
      Promise.all(
        this.isAdmin || this.isManager
          ? [
              this.getEmployees(),
              this.getGeos(),
              this.getProjects(),
              this.getStatuses(),
            ]
          : [this.getStatuses()]
      )
        .then(() => (this.dropdownsLoading = false))
        .catch((err) => {
          this.dropdownsLoading = false;
          console.error(err);
        });
    },
    handleQuery() {
      const currentQuery = this.$route.query;

      if (_.isEmpty(currentQuery)) {
        this.selectedMonth = this.$moment().format("MMMM");
        this.selectedYear = this.$moment().format("YYYY");
      } else {
        if (currentQuery.ppm && currentQuery.ppy) {
          this.selectedMonth = currentQuery.ppm;
          this.selectedYear = currentQuery.ppy;
        } else {
          this.selectedMonth = this.$moment().format("MMMM");
          this.selectedYear = this.$moment().format("YYYY");
        }
        Object.keys(currentQuery).forEach((param) => {
          if (param !== "ppm" && param !== "ppy") {
            this[`filter${param}`] = currentQuery[param];
          }
        });
      }
      this.handleSelectedPeriod(this.selectedMonth, "ppm", true);
      this.handleSelectedPeriod(this.selectedYear, "ppy", true);
      this.getTableData();
    },
    filteredData() {
      return this.$refs.dashboardTable._computedWatchers.filteredData.value;
    },
    downloadInvoice() {
      generateInvoice({
        assignments: this.filteredData(),
        person: this.user.Name,
        total: this.$refs.dashboardTable._computedWatchers.totalEarnings.value,
        filters: this.$route.query,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.cpm-dashboard {
  max-width: 1500px;
  margin: 0 auto;

  &__month-selector {
    width: 120px;
  }

  &__year-selector {
    width: 80px;
  }

  &__date-selector {
    width: 130px;
    &.laptop {
      width: 120px;
    }
  }

  &__project-selector {
    width: 200px;
    &.laptop {
      width: 180px;
    }
    &.laptopSmaller {
      width: 170px;
    }
  }

  &__person-selector {
    width: 170px;
    &.laptop {
      width: 160px;
    }
    &.laptopSmaller {
      width: 150px;
    }
  }

  &__role-selector {
    width: 110px;
  }

  &__geo-selector {
    width: 100px;
  }

  &__status-selector {
    width: 160px;
  }

  &__search-selector {
    width: 340px;
  }
}

::v-deep {
  .selector.v-text-field.v-text-field--enclosed:not(.v-text-field--rounded)
    > .v-input__control
    > .v-input__slot {
    padding-right: 0;
  }
  .selector.v-text-field.v-text-field--solo .v-input__append-inner {
    padding-left: 0;
  }
  .selector.v-autocomplete.v-select.v-input--is-focused input {
    min-width: 0;
  }
  .selector.v-text-field.v-input--dense:not(.v-text-field--outlined) input {
    padding: 2px 0;
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}
.v-enter,
.v-leave-to {
  opacity: 0;
}

@import "~vuetify/src/styles/settings/_variables";

@media #{map-get($display-breakpoints, 'lg-and-down')} {
  .v-application .overline {
    font-size: 0.6rem !important;
  }
}
</style>
